import {
  StyledContent,
  StyledContentLayout,
  StyledReadButton,
  StyledRowLayout,
  StyledSubContent,
} from '@/components/Alarm/NotificationHistoryItem/styles';
import { NotiHistoryData } from '@/types/api';
import { useMessageRead } from '@/hooks/apiHooks';
import useOpenApprovalDocInApp from '@/hooks/approval/useOpenApprovalDocInApp';
import { getApprovalListDeepLinkPath } from '@/utils/approval/approvalFilterLink';
import { useRouter } from 'next/router';
import React from 'react';

type P = {
  mobileMode?: boolean;
  handleReadClick(id: number): void;
  handleOpen(id: string): void;
  openTalkFlowModal?(id: string): void;
  /** 같은 탭에서 다른 화면으로 옮겨 갈 때 알림 목록을 닫는다. */
  closePanel?(): void;
};

const NotificationHistoryItem = ({
  title,
  body,
  watched,
  createdAt,
  link,
  data,
  id,
  mobileMode = false,
  handleReadClick,
  handleOpen,
  openTalkFlowModal,
  closePanel,
}: NotiHistoryData & P) => {
  const router = useRouter();
  const read = watched === 1;
  const popToi = link.replace('//pop.', '//i.');
  const { openDoc, loadingOverlay } = useOpenApprovalDocInApp();

  const handleClick = () => {
    if (link.includes('talkflow')) {
      readMessage();
      const id = link.split('/').pop() ?? '';

      const sendData = JSON.stringify({
        actionType: 'MODAL',
        tabName: 'document',
        docId: id,
      });

      return window.isRNWebview2
        ? window?.ReactNativeWebView?.postMessage(sendData)
        : openTalkFlowModal?.(id);
    }
    const isApproval = popToi.includes('approval');
    const docId = isApproval
      ? new URL(popToi).searchParams.get('doc') ?? ''
      : null;
    const approvalListPath = getApprovalListDeepLinkPath(link);
    const isWebView = window?.isRNWebview;
    const hasMobileAction = isWebView && data?.mobileActionType;

    if (isWebView && hasMobileAction) {
      if (isApproval && docId) {
        // 알림엔 양식 코드가 없어 단건 조회로 판별해야 v2 품의가 v2 화면으로 열림.
        // 재탭 가드는 유틸이 쥔다 — 항목마다 두면 다른 알림을 연달아 눌렀을 때 못 막음.
        // 열리지 않은 탭이면(가드에 걸림) 아래 readMessage 도 건너뛴다 —
        // 열리지도 않은 알림이 읽음 처리되면 안 됨.
        // mobileActionType 은 BE 가 내려주는 문자열이고, 값 자체는 기존처럼 그대로 실어 보낸다.
        const opened = openDoc(docId, {
          actionType: data.mobileActionType as 'MOVE_TAB' | 'MODAL',
        });
        if (!opened) return;
      } else {
        const sendData = JSON.stringify({
          actionType: data.mobileActionType,
          tabName: data.tabName,
          url: link,
        });
        window?.ReactNativeWebView?.postMessage(sendData);
      }
    } else if (isApproval && docId) {
      handleOpen(docId);
    } else if (approvalListPath && !mobileMode) {
      /*
        포탈 안의 결재 목록으로 가는 알림 — 새 탭을 띄우지 않고 그 자리에서 옮겨 간다.
        모바일 목록은 제외한다(PORTAL-14273 은 PC 요청) — 도착 화면이 PC 레이아웃이라
        새 탭으로 열려 모바일 홈이 남아 있는 편이 낫다. 앱 웹뷰는 앞 분기가 이미 가져간다.
      */
      // 홈에 이미 서 있으면 shallow 로 — 탭 전환이 원래 shallow 라우팅이고,
      // 이 경로엔 getServerSideProps 가 걸려 있어 그냥 밀면 서버를 한 번 더 다녀온다.
      closePanel?.();
      router.push(approvalListPath, undefined, {
        shallow: router.pathname === '/',
      });
    } else {
      window.open(popToi, '_blank', 'noopener,noreferrer');
    }
    readMessage();
  };

  const readMessage = () => {
    useMessageRead(id.toString()).then(() => {
      handleReadClick(id);
    });
  };

  const handleRead = (e: React.MouseEvent<HTMLButtonElement>) => {
    readMessage();
    e?.stopPropagation();
  };

  return (
    <>
      <StyledRowLayout {...{ read, mobileMode }} onClick={handleClick}>
        <StyledContentLayout>
          <div style={{ fontWeight: '700' }}>{title}</div>
          <StyledContent>{body}</StyledContent>
          <StyledSubContent>
            <div>{createdAt}</div>
            <StyledReadButton type={'button'} onClick={(e) => handleRead(e)}>
              Mark as read
            </StyledReadButton>
          </StyledSubContent>
        </StyledContentLayout>
      </StyledRowLayout>
      {/* 양식 판별 조회 대기 — 앱 웹뷰에서 결재 문서를 열 때만 뜬다 */}
      {loadingOverlay}
    </>
  );
};

export default NotificationHistoryItem;
